import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Loader2, MonitorPlay, Sparkles } from "lucide-react";

// Doctors enter their certificate number here to replay the moment their
// entry appeared on the Live Pledge Wall (/my-moment/<certificate-id>).
export default function MomentLookupPage() {
  const navigate = useNavigate();
  const [certId, setCertId] = useState("");
  const [loading, setLoading] = useState(false);

  const view = async (e) => {
    e?.preventDefault();
    const id = certId.trim().toUpperCase();
    if (!id) { toast.error("Enter your Certificate ID."); return; }
    setLoading(true);
    try {
      await api.get(`/pledges/moment/${encodeURIComponent(id)}`);
      navigate(`/my-moment/${encodeURIComponent(id)}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Certificate not found");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App bg-slate-50 min-h-screen">
      <Navbar />
      <div className="max-w-xl mx-auto px-5 py-16">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0056D2] mb-6" data-testid="moment-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <form onSubmit={view} className="rounded-3xl bg-white border border-slate-200 p-8 text-center">
          <div className="grid place-items-center w-16 h-16 rounded-full bg-blue-50 mx-auto mb-5">
            <Sparkles className="w-8 h-8 text-[#0056D2]" />
          </div>
          <h1 className="font-display font-extrabold text-3xl text-slate-900">Relive Your Moment</h1>
          <p className="mt-3 text-slate-500 leading-relaxed">
            Enter the Certificate ID from your pledge certificate to see exactly how your entry
            appeared on the Live Pledge Wall — including the pledge count at that very moment.
          </p>
          <div className="mt-8 text-left">
            <Label htmlFor="m-cert" className="text-sm font-medium text-slate-700">Certificate ID</Label>
            <Input
              data-testid="moment-cert-input"
              id="m-cert"
              value={certId}
              onChange={(e) => setCertId(e.target.value)}
              className="mt-1.5 font-mono"
              placeholder="e.g. HIP-2026-AB12CD34"
            />
          </div>
          <Button data-testid="moment-view-btn" type="submit" disabled={loading} size="lg" className="mt-6 w-full rounded-full bg-[#0056D2] hover:bg-blue-700 text-white font-semibold h-12">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking…</> : <><MonitorPlay className="w-4 h-4 mr-2" /> See My Moment on the Live Wall</>}
          </Button>
        </form>
      </div>
      <Footer />
    </div>
  );
}
